import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Mail, Phone, MapPin, Send, MessageSquare, Clock,
  HelpCircle, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const faqData = [
  {
    question: "Comment prendre rendez-vous chez un vétérinaire ?",
    answer: "C'est simple ! Utilisez notre barre de recherche pour trouver un vétérinaire près de chez vous. Sélectionnez la clinique de votre choix, choisissez un créneau disponible et confirmez votre rendez-vous. Vous recevrez une confirmation par email et SMS."
  },
  {
    question: "Le service est-il gratuit ?",
    answer: "Oui, vetoRDV est entièrement gratuit pour les patients ! Vous pouvez prendre autant de rendez-vous que vous le souhaitez sans aucun frais. Seule la consultation chez le vétérinaire est payante."
  },
  {
    question: "Comment annuler ou modifier un rendez-vous ?",
    answer: "Connectez-vous à votre espace patient, accédez à 'Mes rendez-vous' et cliquez sur le rendez-vous que vous souhaitez modifier ou annuler. Nous vous demandons d'annuler au moins 24h à l'avance par respect pour le vétérinaire."
  },
  {
    question: "Puis-je prendre rendez-vous pour plusieurs animaux ?",
    answer: "Oui, vous pouvez ajouter plusieurs animaux à votre profil et prendre des rendez-vous pour chacun d'eux. Lors de la prise de rendez-vous, sélectionnez simplement l'animal concerné."
  },
  {
    question: "Comment fonctionne le rappel de rendez-vous ?",
    answer: "Vous recevrez un rappel automatique par SMS et email la veille de votre rendez-vous. Assurez-vous que vos coordonnées sont bien à jour dans votre espace patient."
  },
  {
    question: "Je suis vétérinaire, comment rejoindre vetoRDV ?",
    answer: "Rendez-vous sur notre page 'Espace Vétérinaire' et créez votre compte professionnel. Notre équipe validera votre inscription sous 48h. L'abonnement est de 35€/mois pour 100 rendez-vous."
  },
  {
    question: "Quels types d'animaux sont acceptés ?",
    answer: "vetoRDV référence des vétérinaires pour tous types d'animaux : chiens, chats, NAC (lapins, furets, rongeurs, reptiles...), équins et animaux d'élevage. Utilisez le filtre 'Par animal' pour trouver un spécialiste."
  },
  {
    question: "Comment trouver un vétérinaire de garde ?",
    answer: "Utilisez notre fonction de géolocalisation et filtrez par 'Urgences'. Les vétérinaires de garde ou ouverts en dehors des heures habituelles seront mis en avant."
  }
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Message envoyé avec succès !');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur de connexion au serveur');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-green-50 via-green-100/50 to-emerald-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Comment pouvons-nous vous aider ?</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Notre équipe est là pour répondre à toutes vos questions. 
              Consultez notre FAQ ou contactez-nous directement.
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-12 -mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Email</h3>
                <p className="text-gray-500 text-sm mb-3">Réponse sous 24h</p>
                <a href="mailto:contact@vetordv.fr" className="text-green-600 hover:underline font-medium">
                  contact@vetordv.fr
                </a>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Téléphone</h3>
                <p className="text-gray-500 text-sm mb-3">Lun-Ven 9h-18h</p>
                <a href="tel:0123456789" className="text-green-600 hover:underline font-medium">
                  01 23 45 67 89
                </a>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Horaires</h3>
                <p className="text-gray-500 text-sm mb-3">Support client</p>
                <p className="text-green-600 font-medium">Lun-Ven : 9h-18h</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ + Contact Form */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* FAQ */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Questions fréquentes</h2>
                </div>
                
                <Accordion type="single" collapsible className="space-y-3">
                  {faqData.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-white rounded-xl px-6 shadow-sm border-none"
                    >
                      <AccordionTrigger className="text-left font-medium text-gray-800 hover:no-underline py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Contact Form */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Nous contacter</h2>
                </div>
                
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">Message envoyé !</h3>
                      <p className="text-gray-500">Nous vous répondrons dans les plus brefs délais.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <Label htmlFor="name">Nom complet</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Jean Dupont"
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="jean@email.fr"
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="subject">Sujet</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          placeholder="Question sur..."
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Décrivez votre demande..."
                          rows={5}
                          required
                          className="mt-1"
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-green-500 hover:bg-green-600 text-white py-6"
                      >
                        {isSubmitting ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                        ) : (
                          <><Send className="w-4 h-4 mr-2" /> Envoyer le message</>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
